import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, Plus, Trash2, Save, ChevronLeft, ChevronRight,
  Clock, Flag, Users, FolderOpen, Pencil
} from 'lucide-react';

const API_URL = '';

// Priority options and styling
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const PRIORITY_STYLES = {
  'Low': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  'Medium': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Urgent': 'bg-red-500/20 text-red-400 border-red-500/30'
};
const PRIORITY_COLORS = {
  'Low': 'bg-slate-500',
  'Medium': 'bg-blue-500',
  'High': 'bg-orange-500',
  'Urgent': 'bg-red-500'
};

// Convert 24hr to 12hr format
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

// Format date to YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
const toLocalDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function CalendarTab({ user, projects, department }) {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    project_id: '',
    priority: 'Medium',
    task_date: '',
    task_time: '09:00',
    task_name: '',
    description: '',
    assigned_employees: []
  });

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/calendar/tasks/department/${department}`);
      setTasks(res.data);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      toast.error('Failed to load calendar tasks');
    } finally {
      setLoading(false);
    }
  }, [department]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calendar/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  }, []);

  useEffect(() => {
    loadTasks();
    loadEmployees();
  }, [loadTasks, loadEmployees]);

  const resetForm = (date = null) => {
    setFormData({
      project_id: '',
      priority: 'Medium',
      task_date: date || toLocalDateStr(new Date()),
      task_time: '09:00',
      task_name: '',
      description: '',
      assigned_employees: []
    });
    setSelectedTask(null);
  };

  const handleNewTask = (date = null) => {
    resetForm(date);
    setShowTaskDialog(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setFormData({
      project_id: task.project_id || '',
      priority: task.priority || 'Medium',
      task_date: task.task_date,
      task_time: task.task_time,
      task_name: task.task_name,
      description: task.description || '',
      assigned_employees: task.assigned_employees || []
    });
    setShowTaskDialog(true);
  };

  const handleSave = async () => {
    if (!formData.task_name) {
      toast.error('Please enter a task name');
      return;
    }
    if (!formData.task_date) {
      toast.error('Please select a date');
      return;
    }
    if (!formData.task_time) {
      toast.error('Please select a time');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        department
      };

      if (selectedTask) {
        await axios.put(`${API_URL}/api/calendar/tasks/${selectedTask.id}`, payload);
        toast.success('Task updated successfully');
      } else {
        await axios.post(`${API_URL}/api/calendar/tasks`, payload);
        toast.success('Task created successfully');
      }
      setShowTaskDialog(false);
      loadTasks();
    } catch (err) {
      console.error('Failed to save task:', err);
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await axios.delete(`${API_URL}/api/calendar/tasks/${taskId}`);
      toast.success('Task deleted');
      loadTasks();
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  };

  const toggleEmployeeAssignment = (emp) => {
    const isAssigned = formData.assigned_employees.some(e => e.id === emp.id);
    if (isAssigned) {
      setFormData({
        ...formData,
        assigned_employees: formData.assigned_employees.filter(e => e.id !== emp.id)
      });
    } else {
      setFormData({
        ...formData,
        assigned_employees: [...formData.assigned_employees, { id: emp.id, full_name: emp.full_name }]
      });
    }
  };

  // Calendar navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar days
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    // Previous month padding
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }

    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  };

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return tasks
      .filter(t => t.task_date === dateStr)
      .sort((a, b) => a.task_time.localeCompare(b.task_time));
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-effect border-border/40">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-primary" />
              <CardTitle>{department} Calendar</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={goToPrevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium min-w-[140px] text-center">
                {formatDate(currentDate)}
              </span>
              <Button variant="outline" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button onClick={() => handleNewTask()} data-testid="btn-new-task">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <div className="border border-border/50 rounded-lg overflow-hidden">
              {/* Week day headers */}
              <div className="grid grid-cols-7 bg-muted/30">
                {weekDays.map(day => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground border-b border-border/50">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((dayObj, idx) => {
                  const dayTasks = getTasksForDate(dayObj.date);
                  const dateStr = toLocalDateStr(dayObj.date);
                  
                  return (
                    <div
                      key={idx}
                      className={`min-h-[100px] p-1 border-b border-r border-border/30 cursor-pointer hover:bg-muted/20 transition-colors ${
                        !dayObj.isCurrentMonth ? 'bg-muted/10 opacity-50' : ''
                      } ${isToday(dayObj.date) ? 'bg-primary/10' : ''}`}
                      onClick={() => handleNewTask(dateStr)}
                    >
                      <div className={`text-xs font-medium mb-1 ${
                        isToday(dayObj.date) ? 'text-primary' : 'text-foreground'
                      }`}>
                        {dayObj.date.getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {dayTasks.slice(0, 3).map(task => (
                          <div
                            key={task.id}
                            className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer ${PRIORITY_STYLES[task.priority]}`}
                            onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                            title={`${formatTime12hr(task.task_time)} - ${task.task_name}\n${task.description || ''}`}
                          >
                            <span className="font-medium">{formatTime12hr(task.task_time)}</span> {task.task_name}
                          </div>
                        ))}
                        {dayTasks.length > 3 && (
                          <div className="text-[10px] text-muted-foreground px-1">
                            +{dayTasks.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Tasks List */}
      <Card className="glass-effect border-border/40">
        <CardHeader>
          <CardTitle className="text-base">Upcoming Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.filter(t => t.task_date >= toLocalDateStr(new Date())).length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">No upcoming tasks</div>
          ) : (
            <div className="space-y-2">
              {tasks
                .filter(t => t.task_date >= toLocalDateStr(new Date()))
                .slice(0, 10)
                .map(task => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-card/30 hover:bg-card/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[task.priority]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{task.task_name}</span>
                          {task.project_name && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground truncate">
                              {task.project_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          <span>{task.task_date}</span>
                          <span>{formatTime12hr(task.task_time)}</span>
                          {task.assigned_employees?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {task.assigned_employees.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditTask(task)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(task.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTask ? 'Edit Task' : 'New Task'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="task_name">Task Name *</Label>
              <Input
                id="task_name"
                data-testid="input-task-name"
                value={formData.task_name}
                onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
                placeholder="Enter task name"
                className="bg-background/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="task_date">Date *</Label>
                <Input
                  id="task_date"
                  type="date"
                  data-testid="input-task-date"
                  value={formData.task_date}
                  onChange={(e) => setFormData({ ...formData, task_date: e.target.value })}
                  className="bg-background/50"
                />
              </div>
              <div>
                <Label htmlFor="task_time">Time *</Label>
                <Input
                  id="task_time"
                  type="time"
                  data-testid="input-task-time"
                  value={formData.task_time}
                  onChange={(e) => setFormData({ ...formData, task_time: e.target.value })}
                  className="bg-background/50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project_id">Project</Label>
                <select
                  id="project_id"
                  data-testid="select-project"
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                >
                  <option value="">No Project</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <select
                  id="priority"
                  data-testid="select-priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm"
                >
                  {PRIORITY_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                data-testid="input-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task description..."
                className="bg-background/50 min-h-[80px]"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                Assign Employees ({formData.assigned_employees.length})
              </Label>
              <div className="max-h-[150px] overflow-y-auto border border-border/50 rounded-md p-2 space-y-1">
                {employees.map(emp => {
                  const isAssigned = formData.assigned_employees.some(e => e.id === emp.id);
                  return (
                    <div
                      key={emp.id}
                      onClick={() => toggleEmployeeAssignment(emp)}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        isAssigned ? 'bg-primary/20 border border-primary/30' : 'hover:bg-muted/30'
                      }`}
                    >
                      <div>
                        <span className="text-sm">{emp.full_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{emp.department || 'N/A'}</span>
                      </div>
                      {isAssigned && (
                        <span className="text-xs text-primary">Assigned</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t border-border/50">
              {selectedTask && (
                <Button
                  variant="destructive"
                  onClick={() => { handleDelete(selectedTask.id); setShowTaskDialog(false); }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="btn-save-task">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Task'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
